import { useState, useRef, useCallback, useEffect } from 'react';
import { tokenManager } from '../services/api';

/**
 * SSE hook for email extraction progress — follows useScrapeStream pattern
 */
const useEmailStream = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [completedStats, setCompletedStats] = useState(null);

  const eventSourceRef = useRef(null);
  const completedRef = useRef(false);

  /**
   * Connect to the email extraction SSE stream.
   * Returns a Promise that resolves when the connection is established.
   */
  const connectToEmailStream = useCallback((jobId) => {
    return new Promise((resolve) => {
      // Disconnect any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const token = tokenManager.getToken();
      if (!token) {
        setConnectionError('No authentication token');
        resolve();
        return;
      }

      // Build SSE URL
      let baseUrl = process.env.REACT_APP_API_URL || '';
      if (baseUrl.endsWith('/api')) baseUrl = baseUrl.slice(0, -4);
      if (!baseUrl.includes('/api')) baseUrl += '/api';

      const url = `${baseUrl}/canvassing/email/stream/${jobId}?token=${encodeURIComponent(token)}`;

      let resolved = false;
      const doResolve = () => {
        if (!resolved) { resolved = true; resolve(); }
      };

      try {
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;
        completedRef.current = false;
        setIsCompleted(false);
        setHasError(false);
        setCompletedStats(null);

        // Timeout fallback
        setTimeout(doResolve, 3000);

        eventSource.onopen = () => {
          setIsConnected(true);
          setConnectionError(null);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case 'connected':
                setIsConnected(true);
                doResolve();
                break;

              case 'heartbeat':
                break;

              case 'progress':
                setProgress(data);
                break;

              case 'completed':
                completedRef.current = true;
                setIsCompleted(true);
                setCompletedStats(data.stats || data);
                setTimeout(() => disconnectFromEmailStream(), 2000);
                break;

              case 'error':
                setHasError(true);
                setConnectionError(data.message);
                break;

              default:
                break;
            }
          } catch (parseError) {
            console.error('Email SSE parse error:', parseError);
          }
        };

        eventSource.onerror = () => {
          if (!completedRef.current) {
            setConnectionError('Connection lost');
            setIsConnected(false);
            doResolve();
            setTimeout(() => {
              if (!completedRef.current && eventSourceRef.current) {
                connectToEmailStream(jobId);
              }
            }, 3000);
          }
        };
      } catch (error) {
        setConnectionError(error.message);
        doResolve();
      }
    });
  }, []);

  /**
   * Disconnect from the SSE stream
   */
  const disconnectFromEmailStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /**
   * Reset all state
   */
  const resetEmailStream = useCallback(() => {
    disconnectFromEmailStream();
    setProgress(null);
    setConnectionError(null);
    setIsCompleted(false);
    setHasError(false);
    setCompletedStats(null);
    completedRef.current = false;
  }, [disconnectFromEmailStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnectFromEmailStream();
  }, [disconnectFromEmailStream]);

  return {
    isConnected,
    connectionError,
    progress,
    isCompleted,
    hasError,
    completedStats,
    connectToEmailStream,
    disconnectFromEmailStream,
    resetEmailStream
  };
};

export default useEmailStream;
